import {
  Button,
  Flex,
  SimpleGrid,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { formatUnits } from '@ethersproject/units';
import Balance from '@ui/components/Balance';
import { InputCard } from '@ui/components/InputCard';
import { useWalletModal } from '@ui/components/Wallet';
import WithWallet from '@ui/components/Wallet/WithWallet';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import useCoinPerStakedCoin from '@ui/hooks/useCoinPerStakedCoin';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import { useLiquidCoinBalance } from '@ui/hooks/useLiquidCoinBalance';
import useUnbondUnlockDate from '@ui/hooks/useUnbondUnlockDate';
import { useTranslations } from '@ui/i18n';
import { MAXW_DESKTOP_CTA } from '@ui/utils/constants';
import { formatNumber, formatTokenAmount } from '@ui/utils/format';
import getBgImage from '@ui/utils/getBgImageProps';
import { currentWallet } from '@veno-app/wallet';
import { format } from 'date-fns';
import type { NextPage } from 'next';
import React, { useEffect, useState } from 'react';

import UnstakeCroDrawer from './UnstakeCroDrawer';
import UnstakeHint from './UnstakeHint';

const { useSupportedChainId } = currentWallet;
export const UnstakeView: NextPage = () => {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[globalCoinMode];
  const maxDecimalsAllowed = Math.min(coinModeInfo.liquidDecimals, 8);

  const { open: onWalletModalOpen } = useWalletModal();
  const supportedChainId = useSupportedChainId();
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const { data: usdValue } = useUnstakeUsdValue(unstakeAmount);
  const [inputError, setInputError] = useState<string>('');
  const { isLoading: balanceLoading, data: balance } = useLiquidCoinBalance();
  const { data: croPerLcro, isLoading: croPerLcroLoading } =
    useCoinPerStakedCoin();
  const { data: unbondUnlockDate } = useUnbondUnlockDate();
  const {
    isOpen: isTransactionDrawerOpen,
    onOpen: onTransactionDrawerOpen,
    onClose: onTransactionDrawerClose,
  } = useDisclosure();

  const maxUnstakeAmount = balance
    ? Math.floor(
        100_000_000 *
          parseFloat(formatUnits(balance, coinModeInfo.liquidDecimals) ?? '0'),
      ) / 100_000_000
    : undefined;

  const croToUnlock = croPerLcro
    ? formatNumber((parseFloat(unstakeAmount) || 0) * (croPerLcro ?? 1))
    : undefined;

  const bgImageProps =
    globalCoinMode === 'atom'
      ? getBgImage('PATTERN_1_ATOM')
      : getBgImage('PATTERN_3');

  const coinAmount =
    balanceLoading || croPerLcroLoading || !balance
      ? undefined
      : balance
          .mul(Math.round((croPerLcro ?? 1) * 100_000_000))
          .div(100_000_000);

  // reset the state if coinMode is changed
  useEffect(() => {
    setUnstakeAmount('');
    setInputError('');
  }, [globalCoinMode]);

  return (
    <>
      <WithWallet
        pt={4}
        pb={8}
        px={4}
        connectHint={t('Connect your wallet to unstake {coin}', {
          coin: coinModeInfo.name,
        })}
        connectWalletProps={{ mt: 4 }}
        bgPos="right -100px"
        minH="572px"
        {...bgImageProps}
      >
        <Flex direction="column" gap={6}>
          <Balance
            title={t('Wallet {coin} Balance', {
              coin: coinModeInfo.liquidName,
            })}
            icon={<coinModeInfo.LiquidIcon />}
            amount={balance}
            unitDecimals={coinModeInfo.liquidDecimals}
            usdNode={`${formatTokenAmount(
              coinAmount,
              4,
              coinModeInfo.decimals,
            )} ${coinModeInfo.name}`}
            showUsdAmount
            mt={4}
          />
          <InputCard
            mt="12px"
            maxDecimals={maxDecimalsAllowed}
            label={t('Input {coin} amount', {
              coin: coinModeInfo.liquidName,
            })}
            max={maxUnstakeAmount}
            errorMessage={inputError}
            value={unstakeAmount}
            usdValue={usdValue}
            onChange={(value) => {
              // maximum of 8 decimals are allowed by the smart contract
              if (
                value &&
                !new RegExp(`^[0-9]+\\.?[0-9]{0,${maxDecimalsAllowed}}$`).test(
                  value,
                )
              ) {
                if (
                  new RegExp(`\\.?[0-9]{${maxDecimalsAllowed + 1},}$`).test(
                    value,
                  )
                ) {
                  setInputError(
                    t('maximum {decimal} decimal places allowed', {
                      decimal: maxDecimalsAllowed,
                    }),
                  );
                } else {
                  setInputError(t('invalid format'));
                }
              } else if (parseFloat(value) < coinModeInfo.minStakeAmount) {
                setInputError(
                  t('minimum value is {amount} {coin}', {
                    amount: coinModeInfo.minStakeAmount,
                    coin: coinModeInfo.liquidName,
                  }),
                );
              } else if (parseFloat(value) > (maxUnstakeAmount ?? 0)) {
                setInputError(
                  t('Not enough {coin} in Wallet', {
                    coin: coinModeInfo.liquidName,
                  }),
                );
              } else {
                setInputError('');
              }
              setUnstakeAmount(value);
            }}
          />
          <UnstakeHint />
          <Button
            w={{ desktop: MAXW_DESKTOP_CTA }}
            mx={{ desktop: 'auto' }}
            onClick={
              supportedChainId ? onTransactionDrawerOpen : onWalletModalOpen
            }
            disabled={!unstakeAmount || !!inputError}
          >
            {t('Unstake')}
          </Button>
          <SimpleGrid columns={2} textStyle="body1" gap={4}>
            <Text>
              {t('{coin} to unlock', {
                coin: coinModeInfo.name,
              })}
            </Text>
            <Flex gap={0.5} justifySelf="end" wordBreak="break-all">
              {croPerLcroLoading ? (
                <Skeleton w="100px" h="24px" />
              ) : (
                <Text textStyle="bodyBold">
                  {croToUnlock} {coinModeInfo.name}
                </Text>
              )}
            </Flex>
            <Text>{t('Withdrawal Fee')}</Text>
            <Text textAlign="end" textStyle="bodyBold">
              0.2%
            </Text>
            <Text>{t('Expected unlock date')}</Text>
            <Flex gap={0.5} justifySelf="end">
              {!unbondUnlockDate ? (
                <Skeleton w="100px" h="24px" />
              ) : (
                <Text textStyle="bodyBold">
                  {format(unbondUnlockDate.toNumber() * 1000, 'dd LLL yyyy')}
                </Text>
              )}
            </Flex>
          </SimpleGrid>
        </Flex>
      </WithWallet>
      <UnstakeCroDrawer
        coinMode={globalCoinMode}
        isOpen={isTransactionDrawerOpen}
        onClose={() => {
          onTransactionDrawerClose();
          setUnstakeAmount('');
        }}
        unstakeAmount={unstakeAmount}
        unbondUnlockDate={unbondUnlockDate}
        croToUnlock={croToUnlock ?? ''}
      />
    </>
  );
};

const useUnstakeUsdValue = (unstakeAmount: string) => {
  const { data: croPerStcro } = useCoinPerStakedCoin();
  const lcroCroValue = croPerStcro
    ? parseFloat(unstakeAmount ?? 0) * croPerStcro
    : undefined;

  return useCoinUsdValue(lcroCroValue);
};
